import { useState, useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL = 10000; // 10 seconds

// Generate a short notification beep using Web Audio API
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
    setTimeout(() => ctx.close(), 500);
  } catch (e) {
    // Audio not available
  }
};

export const useUnreadChat = (api, isLoggedIn) => {
  const [counts, setCounts] = useState({ total: 0, device: 0, boutique_order: 0 });
  const prevTotal = useRef(0);

  const fetchCounts = useCallback(async () => {
    if (!api || !isLoggedIn) return;
    try {
      const res = await api.get('/chat/unread-counts');
      const data = res.data || { total: 0, device: 0, boutique_order: 0 };
      
      // Play sound if new messages arrived
      if (data.total > prevTotal.current && prevTotal.current >= 0) {
        playNotificationSound();
      }
      prevTotal.current = data.total;
      setCounts(data);
    } catch (err) {
      // silently fail
    }
  }, [api, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setCounts({ total: 0, device: 0, boutique_order: 0 });
      prevTotal.current = -1; // Reset so first load doesn't beep
      return;
    }
    
    // Don't beep on first load
    prevTotal.current = -1;
    
    fetchCounts().then(() => {
      // After first fetch, set prevTotal to current so future increases trigger sound
      prevTotal.current = -1; // Will be set properly after second fetch
    });
    
    const interval = setInterval(fetchCounts, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchCounts]);

  // After initial load, enable sound for subsequent changes
  useEffect(() => {
    if (prevTotal.current === -1 && counts.total >= 0) {
      prevTotal.current = counts.total;
    }
  }, [counts]);

  const markRead = useCallback(async (conversationId) => {
    if (!api) return;
    try {
      await api.post(`/chat/conversations/${conversationId}/mark-read`);
      await fetchCounts();
    } catch (err) {
      // silently fail
    }
  }, [api, fetchCounts]);

  return { counts, markRead, refetch: fetchCounts };
};
